import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ApiManager from "../ApiManager/ApiManager";
import CustomSelect from "../components/CustomSelect";
import MapInput from "../components/MapInput";
import MapView from "../components/MapView/MapView";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "leaflet/dist/leaflet.css";
import "../App.css";
import "../components/DriverComponents/AddDriver.css";
import "../components/AddEntity/AddEntity.css";
import "./PublicTripForm.css";
import { useComponentLoading } from "../utils/useComponentLoading";
import { LoadingSpinner } from "../components/LoadingSpinner/LoadingSpinner";
import { getContrastColor } from "../utils/GetContrastColor";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const PublicTripForm = () => {
  const query = useQuery();
  const vendorId = query.get("vendorId");
  const companyId = query.get("companyId");

  const {
    withLoading,
    isLoading,
    getMessage,
    getProgress
  } = useComponentLoading();

  const [metadata, setMetadata] = useState(null);
  const [form, setForm] = useState({});
  const [permissions, setPermissions] = useState({});
  const [options, setOptions] = useState({});
  const [passengers, setPassengers] = useState([{ name: "", phone: "", email: "" }]);
  const [labels, setLabels] = useState([]);
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const [mapReportingAddress, setMapReportingAddress] = useState({});
  const [mapDropAddress, setMapDropAddress] = useState({});
  const [showThankYou, setShowThankYou] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [cities, setCities] = useState([]);
  
  // Address suggestions state for reverse geocoding
  const [reportingAddressSuggestions, setReportingAddressSuggestions] = useState([]);
  const [dropAddressSuggestions, setDropAddressSuggestions] = useState([]);
  
  // City location state for autocomplete
  const [fromCityLocation, setFromCityLocation] = useState(null);
  const [toCityLocation, setToCityLocation] = useState(null);
  
  // Map view toggle state
  const [showMapView, setShowMapView] = useState(false);

  useEffect(() => {
    console.log('PublicTripForm useEffect running', { vendorId, companyId });
    async function fetchMetadata() {
      await withLoading("fetchMetadata", async () => {
        try {
          const res = await ApiManager.getMetadataForCompanyForm(vendorId, companyId);
          if (!res.ok) throw new Error('API error');
          const meta = await res.json();
          const tripDefaults = meta?.metadata?.TripDefaults?.defaultTrip || {};
          setMetadata(meta?.metadata || {});
          setForm(tripDefaults);
          setPermissions(meta?.metadata?.TripDefaults?.fieldPermissions || {});
          setOptions(meta?.metadata?.fieldOptions || {});
          setLabels(meta?.metadata?.fieldOptions?.labels || []);
          
          // Set cities from metadata instead of separate API call
          const fromCities = meta?.metadata?.fieldOptions?.fromCity || [];
          const toCities = meta?.metadata?.fieldOptions?.toCity || meta?.metadata?.fieldOptions?.fromCity || [];
          // Combine and deduplicate cities
          const allCities = [...fromCities];
          if (meta?.metadata?.fieldOptions?.toCity) {
            toCities.forEach(city => {
              if (!allCities.find(c => c.name === city.name)) {
                allCities.push(city);
              }
            });
          }
          setCities(allCities);
          
          const allLabels = meta?.metadata?.fieldOptions?.labels || [];
          let defaultLabels = tripDefaults?.labels || [];
          if (defaultLabels.length > 0 && typeof defaultLabels[0] !== 'object') {
            defaultLabels = allLabels.filter(l => defaultLabels.includes(l.id));
          }
          setSelectedLabelIds(defaultLabels);
          
          if (Array.isArray(tripDefaults?.passengers)) {
            setPassengers(tripDefaults.passengers);
          } else {
            setPassengers([{ name: "", phone: "", email: "" }]);
          }
          
          // Handle default city values that might be objects
          if (tripDefaults?.fromCity) {
            if (typeof tripDefaults.fromCity === 'object' && tripDefaults.fromCity.name) {
              setForm(prev => ({ ...prev, fromCity: tripDefaults.fromCity.name }));
              setFromCityLocation(tripDefaults.fromCity.location || null);
            }
          }
          if (tripDefaults?.toCity) {
            if (typeof tripDefaults.toCity === 'object' && tripDefaults.toCity.name) {
              setForm(prev => ({ ...prev, toCity: tripDefaults.toCity.name }));
              setToCityLocation(tripDefaults.toCity.location || null);
            }
          }
          
          setApiError(false);
        } catch (err) {
          setApiError(true);
        }
      }, "Loading form data...");
    }
    if (vendorId && companyId) fetchMetadata();
  }, [vendorId, companyId]);

  // Cities are now loaded from metadata, no separate API call needed

  // Handle form changes
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Passengers
  const handlePassengerChange = (index, field, value) => {
    const updated = Array.isArray(passengers) ? [...passengers] : [];
    if (updated[index]) {
      updated[index][field] = value;
      setPassengers(updated);
    }
  };
  const addPassenger = () => setPassengers([...passengers, { name: "", phone: "", email: "" }]);
  const removePassenger = (index) => setPassengers(passengers.filter((_, i) => i !== index));

  // Labels
  const handleLabelSelect = (option) => {
    if (!option?.value) return;
    const labelObj = labels.find(l => l.id === option.value);
    if (labelObj && !selectedLabelIds.some(l => l.id === labelObj.id)) {
      setSelectedLabelIds([...(selectedLabelIds || []), labelObj]);
    }
  };
  const handleRemoveLabel = (id) => setSelectedLabelIds((selectedLabelIds || []).filter((label) => label.id !== id));

  // Map Inputs
  const handleReportingAddressSelect = (location) => setMapReportingAddress(location);
  const handleDropAddressSelect = (location) => setMapDropAddress(location);

  // Handler for marker drag end
  const handleMarkerDragEnd = async (id, latlng) => {
    if (id === "reporting") {
      setMapReportingAddress(prev => ({ ...prev, latitude: latlng.latitude, longitude: latlng.longitude }));
    } else if (id === "drop") {
      setMapDropAddress(prev => ({ ...prev, latitude: latlng.latitude, longitude: latlng.longitude }));
    }
    
    // Fetch address suggestions
    try {
      const params = new URLSearchParams({
        lat: latlng.latitude,
        lng: latlng.longitude,
      });
      const response = await ApiManager.getReverseGeocodePublic(params);
      if (response.ok) {
        const data = await response.json();
        if (
          data.success &&
          data.data &&
          Array.isArray(data.data.results) &&
          data.data.results.length > 0
        ) {
          if (id === "reporting") {
            setReportingAddressSuggestions(data.data.results);
          } else if (id === "drop") {
            setDropAddressSuggestions(data.data.results);
          }
        }
      }
    } catch (err) {
      setReportingAddressSuggestions([]);
      setDropAddressSuggestions([]);
    }
  };

  // Handler for selecting an address from suggestions
  const handleSelectAddressSuggestion = (type, suggestion) => {
    if (type === "reporting") {
      setMapReportingAddress(prev => ({
        ...prev,
        description: suggestion.formatted_address,
        latitude: suggestion.location.lat,
        longitude: suggestion.location.lng,
      }));
      setReportingAddressSuggestions([]);
    } else if (type === "drop") {
      setMapDropAddress(prev => ({
        ...prev,
        description: suggestion.formatted_address,
        latitude: suggestion.location.lat,
        longitude: suggestion.location.lng,
      }));
      setDropAddressSuggestions([]);
    }
  };

  // Form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      vendorId: vendorId ?? "",
      companyId: companyId ? Number(companyId) : "",
      carType: form?.carType ?? "",
      tripType: form?.tripType ?? "",
      contractAutoSwitchEnabled: form?.contractAutoSwitchEnabled ?? false,
      garageId: form?.garageId ?? "",
      labels: (selectedLabelIds || []).map(l => l.id),
      operatorNotes: form?.operatorNotes ?? "",
      remarks: form?.remarks ?? "",
      passengers: Array.isArray(passengers) ? passengers.filter((p) => p.name || p.phone || p.email) : [],
      charges: form?.charges ?? {},
      scheduledStartTime: form?.scheduledStartTime ? Math.floor(new Date(form.scheduledStartTime).getTime() / 1000) : undefined,
      scheduledEndTime: form?.scheduledEndTime ? Math.floor(new Date(form.scheduledEndTime).getTime() / 1000) : undefined,
      reportingTime: form?.reportingTime ? Math.floor(new Date(form.reportingTime).getTime() / 1000) : undefined,
      fromCity: typeof form?.fromCity === 'object' ? form?.fromCity?.name || "" : form?.fromCity ?? "",
      toCity: typeof form?.toCity === 'object' ? form?.toCity?.name || "" : form?.toCity ?? "",
      reportingAddress: mapReportingAddress?.description || form?.reportingAddress || "",
      dropAddress: mapDropAddress?.description || form?.dropAddress || "",
      reportingCoordinates: mapReportingAddress?.latitude ? {
        latitude: mapReportingAddress.latitude,
        longitude: mapReportingAddress.longitude
      } : undefined,
      dropCoordinates: mapDropAddress?.latitude ? {
        latitude: mapDropAddress.latitude,
        longitude: mapDropAddress.longitude
      } : undefined,

      bookedBy: {
        name: form?.bookedBy?.name ?? "",
        phone: form?.bookedBy?.phone ?? "",
        email: form?.bookedBy?.email ?? "",
      },
      // Add more fields as needed
    };
    try {
      await withLoading('submitTrip', async () => {
        const res = await ApiManager.createTripViaForm(payload);
        if (res.ok) {
          toast.success("Trip created!");
          setShowThankYou(true);
        } else {
          const err = await res.json();
          toast.error(err.message || "Failed to create trip");
        }
      }, "Submitting trip...");
    } catch (e) {
      toast.error("Network error");
    }
  };

  if (!vendorId || !companyId || apiError) {
    return (
      <div className="publicTripForm">
        <div className="formErrorContainer">
          <div className="errorIcon">❌</div>
          <div className="errorTitle">Form doesn't exist</div>
          <div className="errorMsg">Missing required information in the link.<br/>Please check with VendorBooks for the correct link.</div>
        </div>
      </div>
    );
  }

  if (isLoading("fetchMetadata")) {
    return (
      <div className="publicTripForm">
        <LoadingSpinner
          fullscreen={true}
          message={getMessage("fetchMetadata") || "Loading form data..."}
          progress={getProgress("fetchMetadata")}
          showProgress={true}
        />
      </div>
    );
  }

  if (showThankYou) {
    return (
      <div className="publicTripForm">
        <ToastContainer />
        <div className="thankYouContainer">
          <div className="successTick">✔️</div>
          <h2>Thank you for requesting a trip!</h2>
          <p>Your request has been received. You will be assigned a driver soon.</p>
          <button
            className="bookAnotherTripButton"
            onClick={() => {
              setShowThankYou(false);
              const tripDefaults = metadata?.TripDefaults?.defaultTrip || {};
              setForm(tripDefaults);
              setPassengers(Array.isArray(tripDefaults?.passengers) ? tripDefaults.passengers : [{ name: "", phone: "", email: "" }]);
              let defaultLabels = tripDefaults?.labels || [];
              if (defaultLabels.length > 0 && typeof defaultLabels[0] !== 'object') {
                defaultLabels = (metadata?.fieldOptions?.labels || []).filter(l => defaultLabels.includes(l.id));
              }
              setSelectedLabelIds(defaultLabels);
              setMapReportingAddress({});
              setMapDropAddress({});
            }}
          >
            Request Another Trip
          </button>
        </div>
      </div>
    );
  }

  // Helper for permission
  const isEditable = (field) => permissions?.[field] === "EDITABLE";
  const isViewOnly = (field) => permissions?.[field] === "VIEW_ONLY";
  const isHidden = (field) => permissions?.[field] === "HIDDEN";

  return (
    <div className="publicTripForm">
      <ToastContainer />
      <h2>Request Trip</h2>
      <form onSubmit={handleSubmit}>
        {/* Trip Details Section */}
        <fieldset className="fieldset">
          <legend>Trip Details</legend>
          <div className="row">
            {!isHidden("carType") && (
              <div className="formGroup">
                <label>Car Type <span style={{color: 'red'}}>*</span></label>
                <CustomSelect
                  options={(options.carType || []).map(opt => ({ value: opt.id, label: opt.name }))}
                  value={form.carType}
                  onChange={opt => handleChange("carType", opt?.value)}
                  isDisabled={isViewOnly("carType")}
                  required
                />
              </div>
            )}
            {!isHidden("tripType") && (
              <div className="formGroup">
                <label>Trip Type</label>
                <CustomSelect
                  options={(options.tripType || []).map(opt => ({ value: opt.id, label: opt.name }))}
                  value={form.tripType}
                  onChange={opt => handleChange("tripType", opt?.value)}
                  isDisabled={isViewOnly("tripType")}
                />
              </div>
            )}
            {!isHidden("garageId") && (
              <div className="formGroup">
                <label>Garage</label>
                <CustomSelect
                  options={(options.garageId || []).map(opt => ({ value: opt.id, label: opt.address }))}
                  value={form.garageId}
                  onChange={opt => handleChange("garageId", opt?.value)}
                  isDisabled={isViewOnly("garageId")}
                />
              </div>
            )}
            {!isHidden("contractAutoSwitchEnabled") && (
              <div className="formGroup">
                <label htmlFor="contractAutoSwitchEnabled">Contract Auto Switch</label>
                <input
                  type="checkbox"
                  id="contractAutoSwitchEnabled"
                  name="contractAutoSwitchEnabled"
                  checked={!!form.contractAutoSwitchEnabled}
                  onChange={e => handleChange("contractAutoSwitchEnabled", e.target.checked)}
                  disabled={isViewOnly("contractAutoSwitchEnabled")}
                  style={{ marginLeft: 8 }}
                />
              </div>
            )}
          </div>
        </fieldset>

        {/* Charges Section */}
        {!isHidden("charges") && form.charges && (
          <fieldset className="fieldset">
            <legend>Charges</legend>
            <div className="row">
              {form.charges.baseFare !== undefined && (
                <div className="formGroup">
                  <label>Base Fare</label>
                  <input type="number" className="input" value={form.charges.baseFare} readOnly />
                </div>
              )}
              {form.charges.baseKm !== undefined && (
                <div className="formGroup">
                  <label>Base Km</label>
                  <input type="number" className="input" value={form.charges.baseKm} readOnly />
                </div>
              )}
              {form.charges.extraKmRate !== undefined && (
                <div className="formGroup">
                  <label>Extra Km Rate</label>
                  <input type="number" className="input" value={form.charges.extraKmRate} readOnly />
                </div>
              )}
              {form.charges.driver_daily_allowance !== undefined && (
                <div className="formGroup">
                  <label>Driver Daily Allowance</label>
                  <input type="number" className="input" value={form.charges.driver_daily_allowance} readOnly />
                </div>
              )}
              {form.charges.night_allowance !== undefined && (
                <div className="formGroup">
                  <label>Night Allowance</label>
                  <input type="number" className="input" value={form.charges.night_allowance} readOnly />
                </div>
              )}
              {form.charges.outstation_allowance !== undefined && (
                <div className="formGroup">
                  <label>Outstation Allowance</label>
                  <input type="number" className="input" value={form.charges.outstation_allowance} readOnly />
                </div>
              )}
              {form.charges.outstation_night_allowance !== undefined && (
                <div className="formGroup">
                  <label>Outstation Night Allowance</label>
                  <input type="number" className="input" value={form.charges.outstation_night_allowance} readOnly />
                </div>
              )}
              {form.charges.outstation_overtime_allowance !== undefined && (
                <div className="formGroup">
                  <label>Outstation Overtime Allowance</label>
                  <input type="number" className="input" value={form.charges.outstation_overtime_allowance} readOnly />
                </div>
              )}
              {form.charges.overtime_per_hour !== undefined && (
                <div className="formGroup">
                  <label>Overtime Per Hour</label>
                  <input type="number" className="input" value={form.charges.overtime_per_hour} readOnly />
                </div>
              )}
              {form.charges.sunday_allowance !== undefined && (
                <div className="formGroup">
                  <label>Sunday Allowance</label>
                  <input type="number" className="input" value={form.charges.sunday_allowance} readOnly />
                </div>
              )}
              {form.charges.city !== undefined && (
                <div className="formGroup">
                  <label>City</label>
                  <input type="text" className="input" value={form.charges.city} readOnly />
                </div>
              )}
            </div>
          </fieldset>
        )}

        {/* Labels Section */}
        {!isHidden("labels") && (
          <fieldset className="fieldset">
            <legend>Labels</legend>
            <div className="formGroup">
              <label>Labels</label>
              <CustomSelect
                options={labels.map(label => ({ value: label.id, label: label.name }))}
                value={""}
                onChange={handleLabelSelect}
                isDisabled={isViewOnly("labels")}
                placeholder="Select a Label"
              />
              {selectedLabelIds.length > 0 && (
                <div className="selectedLabels">
                  {selectedLabelIds.map((label) => {
                    if (!label) return null;
                    const backgroundColor = label.color;
                    const textColor = getContrastColor(backgroundColor);
                    return (
                      <div key={label.id} className="selectedLabel" style={{ backgroundColor, color: textColor }}>
                        {label.name}
                        {isEditable("labels") && (
                          <button style={{ color: textColor }} onClick={() => handleRemoveLabel(label.id)} type="button">x</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </fieldset>
        )}

        {/* Operator Notes & Remarks Section */}
        {(!isHidden("operatorNotes") || !isHidden("remarks")) && (
          <fieldset className="fieldset">
            <legend>Notes</legend>
            <div className="row">
              {!isHidden("operatorNotes") && (
                <div className="formGroup">
                  <label>Operator Notes</label>
                  <textarea
                    className="input operatorNotes"
                    value={form.operatorNotes || ""}
                    onChange={e => handleChange("operatorNotes", e.target.value)}
                    disabled={isViewOnly("operatorNotes")}
                  />
                </div>
              )}
              {!isHidden("remarks") && (
                <div className="formGroup">
                  <label>Remarks</label>
                  <textarea
                    className="input remarks"
                    value={form.remarks || ""}
                    onChange={e => handleChange("remarks", e.target.value)}
                    disabled={isViewOnly("remarks")}
                  />
                </div>
              )}
            </div>
          </fieldset>
        )}

        {/* Passengers Section */}
        {!isHidden("passengers") && (
          <fieldset className="fieldset">
            <legend>Passengers</legend>
            {passengers.map((passenger, idx) => (
              <div className="passengerRow" key={idx}>
                <div className="formGroup">
                  <label>Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Name"
                    value={passenger.name}
                    onChange={e => handlePassengerChange(idx, "name", e.target.value)}
                    disabled={isViewOnly("passengers")}
                  />
                </div>
                <div className="formGroup">
                  <label>Phone</label>
                  <input
                    type="tel"
                    className="input"
                    placeholder="Phone"
                    value={passenger.phone}
                    onChange={e => handlePassengerChange(idx, "phone", e.target.value)}
                    disabled={isViewOnly("passengers")}
                  />
                </div>
                <div className="formGroup">
                  <label>Email</label>
                  <input
                    type="email"
                    className="input"
                    placeholder="Email"
                    value={passenger.email}
                    onChange={e => handlePassengerChange(idx, "email", e.target.value)}
                    disabled={isViewOnly("passengers")}
                  />
                </div>
                {isEditable("passengers") && (
                  <div style={{ marginBottom: '20px' }}>
                    <button type="button" className="remove-button" onClick={() => removePassenger(idx)}>Remove Passenger</button>
                  </div>
                )}
              </div>
            ))}
            {isEditable("passengers") && (
                <button type="button" className="custom-button" onClick={addPassenger}>+ Add Passenger</button>
            )}
          </fieldset>
        )}

        {/* From/To City Section (Dropdowns) */}
        {(!isHidden("fromCity") || !isHidden("toCity")) && (
          <fieldset className="fieldset">
            <legend>City Details</legend>
            <div className="row">
              {!isHidden("fromCity") && (
                <div className="formGroup">
                  <label>From City {!isViewOnly("fromCity") && <span style={{color: 'red'}}>*</span>}</label>
                  <CustomSelect
                    options={cities.map(city => ({ value: city.name, label: city.name, location: city.location }))}
                    value={form.fromCity || ""}
                    onChange={opt => {
                      if (!isViewOnly("fromCity")) {
                        handleChange("fromCity", opt?.value);
                        setFromCityLocation(opt?.location || null);
                      }
                    }}
                    placeholder="Select From City"
                    isDisabled={isViewOnly("fromCity")}
                    required={!isViewOnly("fromCity")}
                  />
                </div>
              )}
              {!isHidden("toCity") && (
                <div className="formGroup">
                  <label>To City {!isViewOnly("toCity") && <span style={{color: 'red'}}>*</span>}</label>
                  <CustomSelect
                    options={cities.map(city => ({ value: city.name, label: city.name, location: city.location }))}
                    value={form.toCity || ""}
                    onChange={opt => {
                      if (!isViewOnly("toCity")) {
                        handleChange("toCity", opt?.value);
                        setToCityLocation(opt?.location || null);
                      }
                    }}
                    placeholder="Select To City"
                    isDisabled={isViewOnly("toCity")}
                    required={!isViewOnly("toCity")}
                  />
                </div>
              )}
            </div>
          </fieldset>
        )}

        {/* Address Details Section */}
        {(!isHidden("reportingAddress") || !isHidden("dropAddress")) && (
          <fieldset className="fieldset">
            <legend>Address Details</legend>
            
            {/* Address Inputs (no maps inside) */}
            <div className="mapContainer">
              {/* Reporting Address Section */}
              {!isHidden("reportingAddress") && (
                <div>
                  <MapInput
                    label="Reporting Address"
                    onAddressSelect={isViewOnly("reportingAddress") ? null : handleReportingAddressSelect}
                    name="reportingAddress"
                    cityLocation={fromCityLocation}
                    initialValue={mapReportingAddress}
                    addressSuggestions={reportingAddressSuggestions}
                    onSelectAddressSuggestion={isViewOnly("reportingAddress") ? null : (suggestion => handleSelectAddressSuggestion("reporting", suggestion))}
                    usePublicApi={true}
                    required={!isViewOnly("reportingAddress")}
                  />
                </div>
              )}

              {/* Drop Address Section */}
              {!isHidden("dropAddress") && (
                <div>
                  <MapInput
                    label="Drop Address"
                    onAddressSelect={isViewOnly("dropAddress") ? null : handleDropAddressSelect}
                    name="dropAddress"
                    cityLocation={toCityLocation}
                    initialValue={mapDropAddress}
                    addressSuggestions={dropAddressSuggestions}
                    onSelectAddressSuggestion={isViewOnly("dropAddress") ? null : (suggestion => handleSelectAddressSuggestion("drop", suggestion))}
                    usePublicApi={true}
                  />
                </div>
              )}
            </div>

            {/* Map View Toggle Button */}
            {(mapReportingAddress?.latitude || mapDropAddress?.latitude) && (
              <div className="mapToggleContainer" style={{margin: '20px 0' }}>
                <button
                  type="button"
                  className="custom-button"
                  onClick={() => setShowMapView(!showMapView)}
                  style={{
                    padding: '10px 20px',
                    color: 'black',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {showMapView ? 'Hide Map View' : 'Show Map View'}
                </button>
              </div>
            )}

            {/* Combined MapView for Reporting and Drop Address */}
            {showMapView && (mapReportingAddress?.latitude || mapDropAddress?.latitude) && (
              <div className="mapContainer">
                <MapView
                  markers={[
                    mapReportingAddress && mapReportingAddress.latitude && mapReportingAddress.longitude ? {
                      latitude: mapReportingAddress.latitude,
                      longitude: mapReportingAddress.longitude,
                      label: "Reporting Address",
                      draggable: !isViewOnly("reportingAddress"),
                      id: "reporting"
                    } : null,
                    mapDropAddress && mapDropAddress.latitude && mapDropAddress.longitude ? {
                      latitude: mapDropAddress.latitude,
                      longitude: mapDropAddress.longitude,
                      label: "Drop Address",
                      draggable: !isViewOnly("dropAddress"),
                      id: "drop"
                    } : null
                  ].filter(Boolean)}
                  zoom={13}
                  height={450}
                  onMarkerDragEnd={!isViewOnly("reportingAddress") && !isViewOnly("dropAddress") ? handleMarkerDragEnd : null}
                  showDirections={true}
                  usePublicApi={true}
                />
              </div>
            )}
          </fieldset>
        )}

        {/* Date/Time Section */}
        {(!isHidden("scheduledStartTime") || !isHidden("scheduledEndTime")) && (
          <fieldset className="fieldset">
            <legend>Schedule</legend>
            <div className="row">
              {!isHidden("scheduledStartTime") && (
                <div className="formGroup">
                  <label>Scheduled Start Time {!isViewOnly("scheduledStartTime") && <span style={{color: 'red'}}>*</span>}</label>
                  <input
                    type="datetime-local"
                    className="input"
                    value={form.scheduledStartTime || ""}
                    required={!isViewOnly("scheduledStartTime")}
                    onChange={e => !isViewOnly("scheduledStartTime") && handleChange("scheduledStartTime", e.target.value)}
                    disabled={isViewOnly("scheduledStartTime")}
                  />
                </div>
              )}
              {!isHidden("scheduledEndTime") && (
                <div className="formGroup">
                  <label>Scheduled End Time</label>
                  <input
                    type="datetime-local"
                    className="input"
                    value={form.scheduledEndTime || ""}
                    onChange={e => !isViewOnly("scheduledEndTime") && handleChange("scheduledEndTime", e.target.value)}
                    disabled={isViewOnly("scheduledEndTime")}
                  />
                </div>
              )}
            </div>
          </fieldset>
        )}

        <button
          type="submit"
          className={isLoading('submitTrip') ? 'submitButton loading' : 'submitButton'}
          disabled={isLoading('submitTrip')}
        >
          {isLoading('submitTrip') ? (
            <LoadingSpinner size="xs" inline={true} message="Submitting..." />
          ) : (
            'Submit'
          )}
        </button>
      </form>
    </div>
  );
};

export default PublicTripForm; 